import { useQuery } from '@tanstack/react-query';
import {
  StripeService,
  type PaymentIntentItem,
  type PaymentIntentBooking,
} from '@/services/stripe.service';
import { logger } from '@/utils/logger';

interface UseStripePaymentOptions {
  amount: number;
  currency: string;
  memberId: string;
  locationId: string;
  /** Cart contents, for shop purchases — recorded on the Stripe charge. */
  items?: PaymentIntentItem[];
  /** The booking being paid for, for drop-in fees. */
  booking?: PaymentIntentBooking;
  enabled?: boolean;
}

export const useStripePayment = ({ 
  amount, 
  currency,
  memberId,
  locationId,
  items,
  booking,
  enabled = false // Default to false - only create when explicitly enabled
}: UseStripePaymentOptions) => {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    // Items are part of the key: a changed cart is a different charge and
    // must not reuse an intent describing the old one.
    queryKey: ['stripe-payment-intent', amount, currency, memberId, locationId, items, booking],
    queryFn: async () => {
      try {
        const response = await StripeService.createPaymentIntent({
          amount,
          currency,
          memberId,
          locationId,
          items,
          booking,
        });
        return response;
      } catch (err) {
        logger.error('Failed to create payment intent:', err);
        throw err;
      }
    },
    enabled,
    retry: 2,
    staleTime: 0, // Payment intents should not be cached
    gcTime: 0, // Don't keep in cache after unmount
  });

  return {
    clientSecret: data?.clientSecret,
    externalPaymentId: data?.externalPaymentId,
    publishableKey: data?.publishableKey,
    isLoading,
    error,
    refetch,
  };
};
